import type { DeskContentType } from './content-type';

/**
 * THE THREE AXES. Read this before adding a status value anywhere in Mora.
 *
 * 🔴 A pipeline of ten states is three independent questions flattened into one
 * column. That flattening is fine in a spreadsheet a human maintains by hand;
 * in a database it is a defect generator, and we already shipped the defect:
 * writing an article for a topic the client had APPROVED dragged the row
 * backwards to `review`, because one column was being asked to answer both
 * "how far along is the work" and "what did a human decide". The row had to
 * lie about one of them. A count derived from that column was deleted for the
 * same reason (`editorial-chain.ts`, 2026-09-06).
 *
 * The rule that came out of it, and the reason this file exists:
 *
 *   `status` answers WHERE IS THIS NOW and is not monotonic.
 *   A DECISION is monotonic. Never ask one column for both.
 *
 * So the desk derives a label from three axes that move independently:
 *
 *   PRODUCTION  how far along is the work   project_tasks.status + artifact_kind
 *   REVIEW      what has a human said       approval_items + approval_decisions
 *   RELEASE     is it cleared to go out     approved_at / scheduled_at / posted
 *
 * Nothing here is stored. Every value is computed from rows that already
 * exist, which is why the Desk needed no new pipeline — only this reading of
 * the ones we have.
 *
 * WHO CAN MOVE WHAT is the other half, and it is the founder's guardrail
 * expressed as an invariant rather than a policy: **the client's controls only
 * ever write REVIEW.** RELEASE lives in different tables with a different
 * writer, so "approve" has no path to "published" — not because we check, but
 * because there is no edge. See `applyDecisionWriteBack`, which writes
 * `approved_at` and `project_tasks.status` and nothing else; the effect is only
 * that an item becomes ELIGIBLE for the operator's scheduler.
 *
 * TWO RULES FROM THE CATEGORY RESEARCH, both of which we would otherwise have
 * got wrong:
 *  - **"Overdue" is a computed view everywhere, never a status value.** It is a
 *    filter over a due date. Do not add it to any enum below.
 *  - **Feedback is a separate object related to the item, never a field on it.**
 *    Mora already does this correctly with the append-only `approval_decisions`.
 */

/** How far the WORK has got. Moves forward as Mora or the operator makes things. */
export type ProductionStage = 'idea' | 'drafting' | 'made';

/** What a HUMAN has said. Monotonic within one round; a new round re-opens it. */
export type ReviewStage = 'not_sent' | 'sent' | 'needs_changes' | 'approved' | 'declined';

/** Whether it is cleared to go out. OPERATOR ONLY — the client cannot write here. */
export type ReleaseStage = 'not_ready' | 'ready' | 'scheduled' | 'published';

export interface DeskStage {
  production: ProductionStage;
  review: ReviewStage;
  release: ReleaseStage;
  /**
   * Not a stage. A flag that overrides the label, because "delayed because
   * something is missing" is orthogonal to all three axes — a blocked item
   * still has a real production stage underneath, and un-blocking must not
   * have to reconstruct it.
   */
  blocked: boolean;
  blockedReason?: string | null;
}

/**
 * The single label a card shows. Precedence is right-to-left across the axes —
 * the furthest-along axis that has actually moved wins — with `blocked` on top.
 *
 * The ten labels a person recognises fall out of this. None are stored, and
 * nothing can go backwards when production advances, which was the whole bug.
 */
export type DeskStageKey =
  | 'blocked'
  | 'published'
  | 'scheduled'
  | 'ready'
  | 'approved'
  | 'needs_changes'
  | 'declined'
  | 'waiting'
  | 'made'
  | 'drafting'
  | 'idea';

export function deskStageKey(stage: DeskStage): DeskStageKey {
  if (stage.blocked) return 'blocked';

  if (stage.release === 'published') return 'published';
  if (stage.release === 'scheduled') return 'scheduled';
  if (stage.release === 'ready') return 'ready';

  if (stage.review === 'approved') return 'approved';
  if (stage.review === 'needs_changes') return 'needs_changes';
  if (stage.review === 'declined') return 'declined';
  if (stage.review === 'sent') return 'waiting';

  if (stage.production === 'made') return 'made';
  if (stage.production === 'drafting') return 'drafting';
  return 'idea';
}

/**
 * The same state is two different sentences depending on who is reading, and
 * getting this wrong is how a client is told they are waiting on themselves.
 * There is exactly one state where the wording inverts — `waiting` — but the
 * split is modelled for all of them so no call site has to remember which.
 *
 * Wording obeys CLAUDE.md's product-language rules: no "live" for something
 * merely scheduled, no "launch" for a saved draft, and "Ready to publish"
 * rather than "Ready" so it cannot be read as "ready for you to look at".
 */
export type DeskAudience = 'client' | 'operator';

const STAGE_LABEL: Record<DeskStageKey, Record<DeskAudience, string>> = {
  blocked: { client: 'On hold', operator: 'Blocked' },
  published: { client: 'Published', operator: 'Published' },
  scheduled: { client: 'Scheduled', operator: 'Scheduled' },
  ready: { client: 'Ready to publish', operator: 'Ready to publish' },
  /*
   * 🔴 THE ANONYMOUS FORM IS THE DEFAULT, AND "YOU" HAS TO BE PROVEN.
   * These three used to read "You approved this" / "You asked for changes" /
   * "You skipped this" unconditionally. The room is SHARED and the client is a
   * FIRM, so that told every reader they had personally decided whatever a
   * colleague decided. Saying "you" without knowing who is a claim we cannot
   * support, so the base table no longer makes it — `deskStageLabel` upgrades
   * to "You…" or "<Name>…" only when an author is supplied.
   */
  approved: { client: 'Approved', operator: 'Approved' },
  needs_changes: { client: 'Changes asked for', operator: 'Needs changes' },
  declined: { client: 'Skipped', operator: 'Skipped' },
  waiting: { client: 'Waiting on you', operator: 'Waiting on client' },
  /*
   * 🔴 THE OPERATOR'S THREE PRODUCTION WORDS ARE THE EDITORIAL TABLE'S WORDS.
   *
   * These read "Idea" and "Drafting" until 2026-09-08, while
   * `EditorialTopicTable`'s `EditorialStatus` — the other surface an operator
   * reads, over the SAME `project_tasks` rows — called the identical states
   * "Proposed" and "In progress". Two operator surfaces, one set of rows, two
   * vocabularies: a reader comparing them had to work out for themselves that
   * "Idea" and "Proposed" were the same thing, which is the drift this repo
   * treats as its #1 design smell. Founder, 2026-09-08: _"proposed articles
   * should have … similar status pills … on both the content desk and the main
   * part."_ Editorial's wording wins because it is the word he uses.
   *
   * The CLIENT side is deliberately untouched and deliberately identical
   * across all three: `isVisibleToClient` never lets these stages through, and
   * if one ever leaked, "Being reviewed by us" is the only honest thing to say
   * — a client does not need our production pipeline, they need to know the
   * ball is not in their court.
   */
  made: { client: 'Being reviewed by us', operator: 'Written' },
  drafting: { client: 'Being reviewed by us', operator: 'In progress' },
  idea: { client: 'Being reviewed by us', operator: 'Proposed' },
};

/**
 * Who made the decision this label is describing, when there is one.
 *
 * 🔴 THE CLIENT IS A FIRM, NOT A PERSON, AND THE LABELS ASSUMED OTHERWISE.
 * The first ITC Tax stakeholder is one of several people who could hold a link
 * to that room. `STAGE_LABEL` says "You approved this" to whoever is reading —
 * so the moment a colleague decides something, every other reader is told they
 * personally approved a thing they never opened. That is a false statement
 * rendered to a paying client under their own logo: a No-hallucination failure,
 * not a copy nit.
 *
 * The model is ONE SHARED INBOX with AUTHORED decisions — deliberately not N
 * personal queues. A client firm approves as one voice; the operator needs "the
 * client approved this", not "2 of 3 approved". Consensus rules (who must
 * decide, how many must agree) are what Gain and the enterprise proofing tools
 * ship, and they are the wrong grain for a consultant with one client. But a
 * shared inbox only works if the room can say WHO, which is why the decision
 * carries a name.
 */
export interface DeskDecisionAuthor {
  /** True when the person reading this is the one who decided. */
  byYou?: boolean;
  /** Their first name, when it was somebody else. Never an id. */
  byName?: string | null;
}

/** The three states that describe something a HUMAN did, and so can name them. */
const AUTHORED_KEYS = new Set<DeskStageKey>(['approved', 'needs_changes', 'declined']);

const AUTHORED_LABEL: Partial<
  Record<DeskStageKey, { you: string; other: (n: string) => string; anon: string }>
> = {
  approved: {
    you: 'You approved this',
    other: (name) => `${name} approved this`,
    anon: 'Approved',
  },
  needs_changes: {
    you: 'You asked for changes',
    other: (name) => `${name} asked for changes`,
    anon: 'Changes asked for',
  },
  declined: {
    you: 'You skipped this',
    other: (name) => `${name} skipped this`,
    anon: 'Skipped',
  },
};

/**
 * 🔴 A STATE WORD. NEVER A SENTENCE, AND NEVER A NAME.
 *
 * This used to take a third `author` argument and return "Mark asked for
 * changes" / "You approved this" — which then rendered INSIDE a `StatusBadge`
 * on the calendar list, the table and the board. Founder, 2026-09-08: _"don't
 * [show] 'Mark asked for changes' on a status pill — that's AI slop."_ He is
 * right about the shape: a pill is a one-or-two-word chip you scan a column
 * of, and a full sentence with somebody's name in it is not that. It also
 * wrapped to two lines in a 170px column, which is what made it look broken.
 *
 * The attribution is NOT thrown away — it is real, and it matters, because the
 * room is shared and a client firm has several people who can decide. It moved
 * to `deskDecisionLine`, which returns the sentence for the places a sentence
 * belongs: the item panel and a row's meta line. Deleting the information
 * outright (which one review recommended) would have solved the pill by losing
 * the one fact that makes a shared room legible.
 *
 * So: `deskStageLabel` for chips and columns, `deskDecisionLine` for prose.
 */
export function deskStageLabel(stage: DeskStage, audience: DeskAudience): string {
  return STAGE_LABEL[deskStageKey(stage)][audience];
}

/**
 * The operator's three production words, for a picker that lets someone SET
 * a stage rather than have it derived — e.g. `CalendarMomentDialog`'s
 * "Status" field on a `calendar_moments` row, which has no `project_tasks`
 * row to infer one from. Computed from `STAGE_LABEL` rather than restated, so
 * a picker option can never drift from the word the desk itself renders.
 * Operator audience always: a moment's own dialog is never the client's view.
 */
export const PRODUCTION_STAGE_LABEL: Record<ProductionStage, string> = {
  idea: deskStageLabel(
    { production: 'idea', review: 'not_sent', release: 'not_ready', blocked: false },
    'operator',
  ),
  drafting: deskStageLabel(
    { production: 'drafting', review: 'not_sent', release: 'not_ready', blocked: false },
    'operator',
  ),
  made: deskStageLabel(
    { production: 'made', review: 'not_sent', release: 'not_ready', blocked: false },
    'operator',
  ),
};

/**
 * WHO decided, as a sentence — for the item panel and row meta lines, never
 * for a chip.
 *
 * Returns `null` for anything that is not an authored decision, so a caller
 * renders nothing rather than a blank line. The anonymous form drops the actor
 * instead of guessing it was the reader: telling a colleague they personally
 * approved something they never opened is the false statement this whole
 * `decidedByName` path exists to prevent.
 */
export function deskDecisionLine(
  stage: DeskStage,
  audience: DeskAudience,
  author?: DeskDecisionAuthor,
): string | null {
  const key = deskStageKey(stage);
  if (audience !== 'client' || !AUTHORED_KEYS.has(key) || !author) return null;
  const copy = AUTHORED_LABEL[key];
  if (!copy) return null;
  if (author.byYou) return copy.you;
  if (author.byName) return copy.other(author.byName);
  return copy.anon;
}

/**
 * The tone a `StatusBadge` should carry. Deliberately sparse: most states are
 * neutral, because a board where every column is coloured communicates
 * nothing. Only the two states that ask a person to DO something, plus the two
 * terminal outcomes, earn a colour.
 */
/**
 * Mirrors `StatusBadge`'s own tone union. `destructive` was missing, which is
 * why `blocked` and `declined` had nowhere to go and fell back to warning and
 * neutral — a stopped item and an item nobody had looked at yet rendered the
 * same, which is the one thing a status column exists to prevent.
 */
export type DeskStageTone = 'neutral' | 'success' | 'warning' | 'info' | 'destructive';

/*
 * 🔴 EVERY STATE THAT MEANS SOMETHING DIFFERENT LOOKS DIFFERENT.
 *
 * Four states — `declined`, `made`, `drafting`, `idea` — used to collapse to
 * one grey. That is the exact failure the design system names: *"the agent had
 * followed Law 2 literally and rendered four distinct states as identical grey
 * text. That is not restraint, it is information loss dressed as taste."*
 * The whole job of this column is to tell states apart at a glance.
 *
 * The reference is the founder's own: a Notion approval tracker whose eight
 * statuses each carry their own hue, which is precisely why it scans. Mora's
 * state model is RICHER than that template's (eleven states, derived from three
 * independent axes rather than one hand-maintained select), so it should not be
 * dumbed down to match — it should be as legible.
 *
 * The ladder, and why each rung:
 *   destructive  something stopped         blocked · declined
 *   warning      a person owes an answer   waiting · needs_changes
 *   success      a person said yes         approved · published
 *   info         cleared and queued        ready · scheduled
 *   neutral      we are still working      made · drafting · idea
 *
 * Only the last group stays grey, and that is correct rather than lazy: those
 * three are all "not your problem yet" to a client, and giving each of them a
 * hue would spend colour on a distinction the reader cannot act on.
 */
const STAGE_TONE: Record<DeskStageKey, DeskStageTone> = {
  blocked: 'destructive',
  published: 'success',
  scheduled: 'info',
  ready: 'info',
  approved: 'success',
  needs_changes: 'warning',
  /*
   * Neutral, and that is a deliberate reversal. This was briefly `destructive`
   * on the reasoning that "we are not doing this" is an outcome a reader must
   * be able to find. But red is the wrong word for it: `declined` is the
   * client's own legitimate "no, skip it", not damage and not a failure, and
   * painting their own decision red on their own page misreads it. It also
   * cannot collide with `idea`/`drafting`/`made` on the client's desk, because
   * `isVisibleToClient` never lets those through — the collision that argued
   * for a hue only exists on the operator's side, where the board column
   * already carries it.
   */
  declined: 'neutral',
  waiting: 'warning',
  made: 'neutral',
  drafting: 'neutral',
  idea: 'neutral',
};

export function deskStageTone(stage: DeskStage): DeskStageTone {
  return STAGE_TONE[deskStageKey(stage)];
}

/**
 * WHAT THE CLIENT MAY SEE.
 *
 * Every client portal in this category hides the internal half of the pipeline:
 * the client enters the workflow at the moment something is SENT to them, and
 * never sees Idea / Drafting / Internal review. That is not squeamishness — a
 * client watching a draft get rewritten four times reads it as chaos, and it
 * invites comment on work that is not finished.
 *
 * So this is the gate, in one place, rather than a filter re-derived at every
 * call site. An item is on the client's desk when the operator has PUSHED it
 * there (an `approval_items` row exists, which is what makes `review` leave
 * `not_sent`) — never merely because it exists in the workspace.
 */
export function isVisibleToClient(stage: DeskStage): boolean {
  return stage.review !== 'not_sent';
}

/**
 * The board columns, left to right. Fewer than the eleven possible keys,
 * because a board with eleven columns is a spreadsheet: `made`/`drafting`/
 * `idea` collapse into one "In progress" column, and `declined` sits with
 * `needs_changes` under "Needs attention" since both mean the same thing to
 * whoever has to act next.
 */
export type DeskColumn =
  'in_progress' | 'waiting' | 'needs_attention' | 'approved' | 'scheduled' | 'published';

export const DESK_COLUMNS: readonly DeskColumn[] = [
  'in_progress',
  'waiting',
  'needs_attention',
  'approved',
  'scheduled',
  'published',
] as const;

export const DESK_COLUMN_LABEL: Record<DeskColumn, Record<DeskAudience, string>> = {
  in_progress: { client: 'With us', operator: 'In progress' },
  waiting: { client: 'Waiting on you', operator: 'Waiting on client' },
  needs_attention: { client: 'You asked for changes', operator: 'Needs attention' },
  approved: { client: 'Approved', operator: 'Approved' },
  scheduled: { client: 'Scheduled', operator: 'Scheduled' },
  published: { client: 'Published', operator: 'Published' },
};

/**
 * `blocked` deliberately does NOT get its own column. It is a flag, and an item
 * that is blocked is still somewhere in the pipeline — moving it to a Blocked
 * column would lose that, and it is exactly the mistake of turning a modifier
 * into a stage. It renders as a badge on the card, in whichever column the
 * underlying stage puts it.
 */
/**
 * The tone a COLUMN carries. Colour belongs on the column, not repeated on
 * every card inside it — a board where 32 cards each announce the state their
 * own heading already gives is the duplication that made the first version read
 * as noise, and greying all six is what made the second read as dead.
 *
 * 🔴 TONE DEPENDS ON THE AUDIENCE, BECAUSE "ON YOU" DOES.
 *
 * This was a flat `Record<DeskColumn, DeskStageTone>` giving BOTH `waiting` and
 * `needs_attention` the warning hue, on the stated grounds that they are "the
 * two that ask a person to act". They are — but never the SAME person, and the
 * labels one screen up have always known it: `waiting` reads "Waiting on you"
 * to a client and "Waiting on client" to an operator. The words inverted with
 * the audience and the colour did not.
 *
 * Measured on the real ITC Tax desk: the operator's board painted "Waiting on
 * client" (30 items) and "Needs attention" (2 items) in the identical
 * `rgb(179, 83, 9)`. Thirty cards the operator cannot act on shouted exactly as
 * loudly as the two that were actually his to fix, which is the precise inverse
 * of what the hue is for. Amber that lands on the biggest column stops being a
 * signal and becomes the background.
 *
 * So: exactly ONE column is warning per audience, and it is the one whose ball
 * is in that reader's court. The other goes neutral — not because its state is
 * unimportant, but because it is somebody else's move.
 */
export function deskColumnTone(column: DeskColumn, audience: DeskAudience): DeskStageTone {
  switch (column) {
    case 'waiting':
      // The client owes a decision. Theirs to act on, ours to wait for.
      return audience === 'client' ? 'warning' : 'neutral';
    case 'needs_attention':
      // The client already spoke ("You asked for changes"); the work is ours.
      return audience === 'operator' ? 'warning' : 'neutral';
    case 'approved':
    case 'published':
      return 'success';
    case 'scheduled':
      return 'info';
    default:
      return 'neutral';
  }
}

export function deskColumn(stage: DeskStage): DeskColumn {
  const key = deskStageKey({ ...stage, blocked: false });
  switch (key) {
    case 'published':
      return 'published';
    case 'scheduled':
      return 'scheduled';
    case 'ready':
    case 'approved':
      return 'approved';
    case 'needs_changes':
    case 'declined':
      return 'needs_attention';
    case 'waiting':
      return 'waiting';
    default:
      return 'in_progress';
  }
}

// ---------------------------------------------------------------------------
// Derivation from real rows
// ---------------------------------------------------------------------------

export interface DeskStageInput {
  /** `project_tasks.status`, when the item is a plan/editorial row. */
  taskStatus?: string | null;
  /** `project_tasks.artifact_kind` — non-null once the artifact exists. */
  artifactKind?: string | null;
  /**
   * An explicit, operator-set production stage — wins over every inference
   * below. The only input a `calendar_moments` row can supply: a moment has
   * no `project_tasks` row to read a `taskStatus`/`artifactKind` from, so
   * without this it would always derive `idea` regardless of how far along
   * the work actually is (see `calendar_moments.production_stage`,
   * 20260916210000).
   */
  productionOverride?: ProductionStage | null;
  /** True when an `approval_items` row exists, i.e. the operator pushed it. */
  sentForReview?: boolean;
  /** The newest `approval_decisions.verdict` for this item, if any. */
  latestVerdict?: string | null;
  /** `social_posts.approved_at` — set by the client's approval write-back. */
  approvedAt?: string | null;
  /** `social_posts.scheduled_at`. */
  scheduledAt?: string | null;
  /** `social_posts.status` — 'published' / 'posted' once it has gone out. */
  postStatus?: string | null;
  /** Operator's release gate: the publish checklist is complete. */
  readyToPublish?: boolean;
  blocked?: boolean;
  blockedReason?: string | null;
}

function productionFrom(input: DeskStageInput): ProductionStage {
  if (input.productionOverride) return input.productionOverride;
  if (input.artifactKind) return 'made';
  switch (input.taskStatus) {
    case 'drafting':
      return 'drafting';
    case 'review':
    case 'approved':
    case 'scheduled':
    case 'published':
    case 'partially_published':
      // A row that reached any of these necessarily had something to act on.
      return 'made';
    default:
      return 'idea';
  }
}

function reviewFrom(input: DeskStageInput): ReviewStage {
  if (!input.sentForReview) return 'not_sent';
  switch (input.latestVerdict) {
    case 'approved':
    case 'answered':
      return 'approved';
    case 'changes_requested':
      return 'needs_changes';
    case 'declined':
      return 'declined';
    default:
      return 'sent';
  }
}

function releaseFrom(input: DeskStageInput): ReleaseStage {
  if (input.postStatus === 'published' || input.postStatus === 'posted') return 'published';
  if (input.scheduledAt) return 'scheduled';
  if (input.readyToPublish) return 'ready';
  return 'not_ready';
}

/**
 * 🔴 `approvedAt` is NOT read as a release signal, and that is the guardrail.
 *
 * It is tempting to treat "the client approved" as "ready", and it is exactly
 * the mistake the founder asked us to avoid: *"just because he clicks approve,
 * I don't want it to automatically move forward."* Approval is a REVIEW fact.
 * It is carried on `approvedAt` only so the operator's scheduler can find the
 * item — eligibility, not release. Release advances when a human on our side
 * ticks the checklist, schedules, or publishes.
 */
export function deriveDeskStage(input: DeskStageInput): DeskStage {
  return {
    production: productionFrom(input),
    review: reviewFrom(input),
    release: releaseFrom(input),
    blocked: Boolean(input.blocked),
    blockedReason: input.blockedReason ?? null,
  };
}

export interface DeskItemSummary {
  stage: DeskStage;
  key: DeskStageKey;
  column: DeskColumn;
  contentType: DeskContentType;
}
